import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCallback, useEffect, useState } from "react";
import { Ibge } from "../../../services/Ibge";
import { ISelectOptions } from "../../../types/inputs";
import { toast } from "react-toastify";
import { useContextSite } from "../../../context/Context";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pagamento } from "../../../services/Pagamento";
import { maskTime } from "../../../utils/masks";

import { IDeliveryForm } from "../../../types/delivery";
import { Delivery } from "../../../services/Delivery";

const schemaForm = z.object({
  horarioFinal: z.string().min(1, "Campo obrigatorio"),
  horarioFinalAlmoco: z.string().optional(),
  horarioFinalFds: z.string().optional(),
  horarioInicial: z.string().min(1, "Campo obrigatorio"),
  horarioInicialAlmoco: z.string().optional(),
  horarioInicialFds: z.string().optional(),
  margemAtendimento: z.string().min(1, "Campo obrigatorio"),
  quantidadeVagas: z.number(),
  quantidadeVagasConcessionaria: z.number(),
  tempoMedio: z.string().min(1, "Campo obrigatorio"),
  cidade: z.string().min(1, "Campo obrigatorio"),
  uf: z.string().min(1, "Campo obrigatorio"),
  bloquearAgendamentosMesmoDia: z.boolean(),
  uuidMunicipiosLimitrofes: z.array(z.string()),
  uuidContaIugu: z.string().min(1, "Campo obrigatorio"),
});

export const useAdminDeliveryRegister = () => {
  const [ufs, setUfs] = useState<ISelectOptions[]>([] as ISelectOptions[]);
  const [cidades, setCidades] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const [searchParam] = useSearchParams();
  const lojaId = searchParam.get("id");
  const [cidadeTemp, setCidadeTemp] = useState("");
  const { setIsLoad } = useContextSite();
  const navigate = useNavigate();
  const [contas, setContas] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IDeliveryForm>({
    defaultValues: {
      horarioFinal: "",
      horarioFinalAlmoco: "",
      horarioFinalFds: "",
      horarioInicial: "",
      horarioInicialAlmoco: "",
      horarioInicialFds: "",
      margemAtendimento: "",
      quantidadeVagas: 0,
      tempoMedio: "",
      cidade: "",
      uf: "",
      uuidContaIugu: "",
      quantidadeVagasConcessionaria: 0,
      bloquearAgendamentosMesmoDia: false,
      uuidMunicipiosLimitrofes: [] as string[],
    },
    mode: "all",
    resolver: zodResolver(schemaForm),
  });

  function submitForm(data: IDeliveryForm) {
    setIsLoad(true);

    Delivery.cadastro(data)
      .then(() => navigate("/configuracoes/deliverys"))
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      )
      .finally(() => setIsLoad(false));
  }

  const getUfs = useCallback(() => {
    Ibge.UFs()
      .then(({ data }) => {
        const options = data.map((i) => ({
          value: i.sigla,
          label: i.sigla,
          element: i,
        }));

        setUfs(options);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          toast.error(mensagem);
        }
      );
  }, []);

  useEffect(() => {
    if (watch("horarioInicial")) {
      setValue("horarioInicial", maskTime(watch("horarioInicial")));
    }
  }, [watch("horarioInicial")]);

  useEffect(() => {
    if (watch("horarioFinal")) {
      setValue("horarioFinal", maskTime(watch("horarioFinal")));
    }
  }, [watch("horarioFinal")]);

  useEffect(() => {
    if (watch("horarioInicialAlmoco")) {
      setValue("horarioInicialAlmoco", maskTime(watch("horarioInicialAlmoco")));
    }
  }, [watch("horarioInicialAlmoco")]);

  useEffect(() => {
    if (watch("horarioFinalAlmoco")) {
      setValue("horarioFinalAlmoco", maskTime(watch("horarioFinalAlmoco")));
    }
  }, [watch("horarioFinalAlmoco")]);

  useEffect(() => {
    if (watch("horarioInicialFds")) {
      setValue("horarioInicialFds", maskTime(watch("horarioInicialFds")));
    }
  }, [watch("horarioInicialFds")]);

  useEffect(() => {
    if (watch("horarioFinalFds")) {
      setValue("horarioFinalFds", maskTime(watch("horarioFinalFds")));
    }
  }, [watch("horarioFinalFds")]);

  useEffect(() => {
    if (watch("tempoMedio")) {
      setValue("tempoMedio", maskTime(watch("tempoMedio")));
    }
  }, [watch("tempoMedio")]);

  useEffect(() => {
    if (watch("margemAtendimento")) {
      setValue("margemAtendimento", maskTime(watch("margemAtendimento")));
    }
  }, [watch("margemAtendimento")]);

  const getContas = useCallback(() => {
    Pagamento.listarContas()
      .then(({ data }) => {
        const options = data.map((_) => ({
          value: _.uuid,
          label: _.nome,
          element: _,
        }));

        setContas(options);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      );
  }, []);

  useEffect(() => {
    getUfs();
    getContas();
  }, []);

  useEffect(() => {
    if (watch("uf")) {
      setValue("cidade", "");
      Ibge.CidadesPorEstado({ sigla: watch("uf") })
        .then(({ data }) => {
          const options = data.map((item) => ({
            value: item.nome,
            label: item.nome,
            element: item,
          }));
          setCidades(options);
          setValue("cidade", cidadeTemp);
        })
        .then(() => {
          setCidadeTemp("");
        })
        .catch((erro) => toast.error("Erro ao requisitar a lista de cidades"));
    }
  }, [watch("uf")]);

  return {
    Controller,
    control,
    handleSubmit,
    errors,
    register,
    submitForm,
    ufs,
    cidades,
    lojaId,
    contas,
    watch,
  };
};
